import { appendAuditLog } from './auditLog'
import { resolvePlaceTimezone } from '@shared/lib/businessDay'
import {
  mergeSharedPlaceSettings,
  type SharedPlaceSettings,
  type CurrencyCode as SharedCurrencyCode,
} from '@shared/lib/sharedPlaceSettings'
import {
  mergeOrderModeSettings,
  type OrderModeSettings,
} from '@shared/lib/orderModeSettings'
import {
  type ChimeSourcePrefs,
} from './orderChimePolicy'
import { useFirestore } from './firebase'
import * as firestoreSettings from './firestore/settings'
import { mergeInvoiceProfile, type InvoiceProfileV1 } from './invoiceProfile'
import type { PrintFormatBundleV1 } from './printFormats/types'
import { mergePrintFormatBundle } from './printFormats/types'
import { defaultPlaceSettings } from '@shared/lib/placeDefaults'

export type { InvoiceProfileV1 } from './invoiceProfile'
export type { SharedPlaceSettings } from '@shared/lib/sharedPlaceSettings'
export type { OrderModeSettings } from '@shared/lib/orderModeSettings'

const STORAGE_KEY = 'wortelogy-settings'

/** Currency id stored in settings. Add new entries to CURRENCIES to support more. */
export type CurrencyCode = SharedCurrencyCode

export interface CurrencyInfo {
  id: CurrencyCode
  label: string
  /** Prefix shown in front of amount (e.g. "Rp. ", "$", "S$"). */
  symbol: string
  /** Locale for number formatting (e.g. 'id-ID', 'en-US'). */
  locale: string
  /** Decimal places for amount (0 for IDR, 2 for USD/SGD). */
  fractionDigits: number
}

/** Single source of truth for supported currencies. Add here to scale. */
export const CURRENCIES: CurrencyInfo[] = [
  { id: 'rupiah', label: 'Rupiah (IDR)', symbol: 'Rp. ', locale: 'id-ID', fractionDigits: 0 },
  { id: 'dollar', label: 'Dollar (USD)', symbol: '$', locale: 'en-US', fractionDigits: 2 },
  { id: 'sgd', label: 'Singapore Dollar (SGD)', symbol: 'S$', locale: 'en-SG', fractionDigits: 2 },
]

const CURRENCY_IDS = CURRENCIES.map((c) => c.id)
const CURRENCY_BY_ID = new Map(CURRENCIES.map((c) => [c.id, c]))

function isCurrencyCode(value: string): value is CurrencyCode {
  return CURRENCY_IDS.includes(value as CurrencyCode)
}

export interface OnProcessAction {
  id: string
  label: string
  type: string
}

export interface Settings {
  confirmMoveToProcessingMessage: string
  onProcessActions: OnProcessAction[]
  /** @deprecated use shared.currency — kept for legacy Firestore docs */
  currency: CurrencyCode
  /** Place-wide settings synced across devices (timezone, hours, currency). */
  shared: SharedPlaceSettings
  /** Place-wide operating mode and atomic order/payment/table rules. */
  orderModeSettings: OrderModeSettings
  printFormats: PrintFormatBundleV1
  /** Business details for document invoices (logo, address, socials). */
  invoiceProfile?: InvoiceProfileV1
  /** New pending order chime volume: 0 = muted, 1 = full. */
  newOrderChimeVolume: number
  /** Chime when pending/processing orders arrive from this POS device. */
  chimeOnPendingFromThisDevice: boolean
  /** Chime when orders arrive from the customer app. */
  chimeOnPendingFromCustomerApp: boolean
  /** Chime when orders arrive from another POS device. */
  chimeOnPendingFromOtherPos: boolean
  /** Chime when orders arrive from delivery integrations (future). */
  chimeOnPendingFromDelivery: boolean
}

export type { ChimeSourcePrefs }

export const DEFAULT_SETTINGS: Settings = defaultPlaceSettings()

function clampChimeVolume(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_SETTINGS.newOrderChimeVolume
  }
  return Math.min(1, Math.max(0, value))
}

function mergeChimeBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function mergeSettings(parsed: Partial<Settings> | null): Settings {
  if (!parsed) return { ...DEFAULT_SETTINGS }
  const legacyCurrency =
    parsed.currency && isCurrencyCode(parsed.currency) ? parsed.currency : undefined
  const shared = mergeSharedPlaceSettings({
    ...parsed.shared,
    currency: parsed.shared?.currency ?? legacyCurrency,
  })
  return {
    confirmMoveToProcessingMessage:
      parsed.confirmMoveToProcessingMessage ??
      DEFAULT_SETTINGS.confirmMoveToProcessingMessage,
    onProcessActions:
      (parsed.onProcessActions?.length ?? 0) > 0
        ? parsed.onProcessActions!
        : DEFAULT_SETTINGS.onProcessActions,
    currency: shared.currency,
    shared,
    orderModeSettings: mergeOrderModeSettings(parsed.orderModeSettings),
    printFormats: mergePrintFormatBundle(parsed.printFormats),
    invoiceProfile: mergeInvoiceProfile(parsed.invoiceProfile),
    newOrderChimeVolume: clampChimeVolume(parsed.newOrderChimeVolume),
    chimeOnPendingFromThisDevice: mergeChimeBool(
      parsed.chimeOnPendingFromThisDevice,
      DEFAULT_SETTINGS.chimeOnPendingFromThisDevice,
    ),
    chimeOnPendingFromCustomerApp: mergeChimeBool(
      parsed.chimeOnPendingFromCustomerApp,
      DEFAULT_SETTINGS.chimeOnPendingFromCustomerApp,
    ),
    chimeOnPendingFromOtherPos: mergeChimeBool(
      parsed.chimeOnPendingFromOtherPos,
      DEFAULT_SETTINGS.chimeOnPendingFromOtherPos,
    ),
    chimeOnPendingFromDelivery: mergeChimeBool(
      parsed.chimeOnPendingFromDelivery,
      DEFAULT_SETTINGS.chimeOnPendingFromDelivery,
    ),
  }
}

export function getSettings(): Settings {
  if (useFirestore()) return mergeSettings(firestoreSettings.getSettingsCache())
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    return mergeSettings(JSON.parse(raw) as Partial<Settings>)
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export function getSharedSettings(): SharedPlaceSettings {
  return getSettings().shared
}

/** IANA timezone from place settings (falls back to device zone, then UTC). */
export function placeTimezone(): string {
  return resolvePlaceTimezone(getSharedSettings())
}

export function saveSettings(
  patch: Partial<Omit<Settings, 'shared' | 'orderModeSettings'>> & {
    shared?: Partial<SharedPlaceSettings>
    orderModeSettings?: Partial<OrderModeSettings>
  },
): void {
  const current = getSettings()
  const sharedPatch = patch.shared
  const orderModeSettingsPatch = patch.orderModeSettings
  const nextShared = sharedPatch
    ? mergeSharedPlaceSettings({ ...current.shared, ...sharedPatch })
    : current.shared
  const nextOrderModeSettings = orderModeSettingsPatch
    ? mergeOrderModeSettings({ ...current.orderModeSettings, ...orderModeSettingsPatch })
    : current.orderModeSettings
  const nextCurrency =
    patch.currency && isCurrencyCode(patch.currency) ? patch.currency : nextShared.currency
  const next: Settings = {
    ...current,
    ...patch,
    onProcessActions: patch.onProcessActions ?? current.onProcessActions,
    shared: mergeSharedPlaceSettings({ ...nextShared, currency: nextCurrency }),
    orderModeSettings: nextOrderModeSettings,
    currency: nextCurrency,
    printFormats: patch.printFormats ?? current.printFormats,
    invoiceProfile: patch.invoiceProfile ?? current.invoiceProfile,
    newOrderChimeVolume: patch.newOrderChimeVolume ?? current.newOrderChimeVolume,
    chimeOnPendingFromThisDevice:
      patch.chimeOnPendingFromThisDevice ?? current.chimeOnPendingFromThisDevice,
    chimeOnPendingFromCustomerApp:
      patch.chimeOnPendingFromCustomerApp ?? current.chimeOnPendingFromCustomerApp,
    chimeOnPendingFromOtherPos:
      patch.chimeOnPendingFromOtherPos ?? current.chimeOnPendingFromOtherPos,
    chimeOnPendingFromDelivery:
      patch.chimeOnPendingFromDelivery ?? current.chimeOnPendingFromDelivery,
  }
  try {
    if (useFirestore()) {
      firestoreSettings.saveSettings(next)
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      window.dispatchEvent(new CustomEvent('wortelogy-settings-changed'))
    }
    const keys = Object.keys(patch).join(', ')
    appendAuditLog({
      domain: 'settings',
      action: 'settings.updated',
      message: keys ? `Updated settings (${keys})` : 'Updated settings',
      entityType: 'settings',
      entityId: 'app',
      meta: { keys },
    })
  } catch (e) {
    console.error('saveSettings failed', e)
  }
}

/** Format a price for display: symbol + localized amount (e.g. "Rp. 27.500", "$27.50"). */
export function formatPrice(amount: number, currency: CurrencyCode): string {
  if (!Number.isFinite(amount)) return '—'
  const info = CURRENCY_BY_ID.get(currency)
  if (!info) return String(amount)
  const value =
    info.fractionDigits === 0
      ? Math.round(amount).toLocaleString(info.locale, { maximumFractionDigits: 0 })
      : amount.toLocaleString(info.locale, {
          minimumFractionDigits: info.fractionDigits,
          maximumFractionDigits: info.fractionDigits,
        })
  return `${info.symbol}${value}`
}
